<template>
  <div
    class="accruals-estimate-activity-tab"
    data-testid="accruals-estimate-activity-tab"
  >
    <div
      v-if="loading"
      class="accruals-estimate-activity-tab__loading"
      data-testid="accruals-estimate-activity-tab__loading"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="32"
      />
    </div>

    <p
      v-else-if="!items.length"
      class="accruals-estimate-activity-tab__empty"
      data-testid="accruals-estimate-activity-tab__empty"
    >
      {{ emptyMessage || 'No activity yet for this estimate.' }}
    </p>

    <div
      v-else
      class="accruals-estimate-activity-tab__list"
      data-testid="accruals-estimate-activity-tab__timeline"
    >
      <div
        v-for="(entry, index) in items"
        :key="entry.id || index"
        class="accruals-estimate-activity-tab__entry"
        :data-testid="`accruals-estimate-activity-tab__entry--${index}`"
      >
        <div class="accruals-estimate-activity-tab__entry-time">
          <span class="accruals-estimate-activity-tab__time-text">{{ entry.timeLabel || '—' }}</span>
          <span class="accruals-estimate-activity-tab__date-text">{{ entry.dateLabel }}</span>
        </div>
        <div class="accruals-estimate-activity-tab__entry-line" aria-hidden="true">
          <div
            class="accruals-estimate-activity-tab__line-top"
            :class="{ 'accruals-estimate-activity-tab__line-top--visible': index !== 0 }"
          />
          <div class="accruals-estimate-activity-tab__dot" />
          <div
            class="accruals-estimate-activity-tab__line-bottom"
            :class="{ 'accruals-estimate-activity-tab__line-bottom--visible': index !== items.length - 1 }"
          />
        </div>
        <div class="accruals-estimate-activity-tab__content">
          <p class="accruals-estimate-activity-tab__title">{{ entry.text }}</p>
          <div
            v-if="entry.userName || entry.email"
            class="accruals-estimate-activity-tab__user-row"
          >
            <v-tooltip bottom content-class="tooltip-bottom">
              <template #activator="{ on, attrs }">
                <span
                  class="accruals-estimate-activity-tab__avatar"
                  v-bind="attrs"
                  v-on="on"
                >{{ avatarLetter(entry) }}</span>
              </template>
              <span>{{ entry.userName || entry.email }}</span>
            </v-tooltip>
            <span
              v-if="entry.email || entry.userName"
              class="accruals-estimate-activity-tab__user-email"
            >{{ entry.email || entry.userName }}</span>
          </div>
          <div
            v-if="entry.detail"
            class="accruals-estimate-activity-tab__detail"
          >
            <p class="accruals-estimate-activity-tab__detail-text">{{ entry.detail }}</p>
          </div>
          <div
            v-if="entry.files && entry.files.length"
            class="accruals-estimate-activity-tab__files"
          >
            <button
              v-for="file in entry.files"
              :key="file.id"
              type="button"
              class="accruals-estimate-activity-tab__file"
              :data-testid="`accruals-estimate-activity-tab__file--${file.id}`"
              @click="downloadFile(file)"
            >
              {{ file.name }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <form
      v-if="canCompose"
      class="accruals-estimate-activity-tab__composer"
      data-testid="accruals-estimate-activity-tab__composer"
      @submit.prevent="onSend"
    >
      <span class="accruals-estimate-activity-tab__composer-avatar">{{ initials }}</span>
      <input
        :value="draft"
        type="text"
        class="accruals-estimate-activity-tab__input"
        placeholder="Add a note... type @ to mention"
        data-testid="accruals-estimate-activity-tab__input"
        @input="$emit('update:draft', $event.target.value)"
      />
      <base-button
        primary
        small
        type="submit"
        class="shadow-off"
        :disabled="!canSend"
        data-testid="accruals-estimate-activity-tab__send"
      >
        {{ sending ? 'Sending' : 'Send' }}
      </base-button>
    </form>
  </div>
</template>

<script>
export default {
  name: 'AccrualsEstimateActivityTab',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      default: '',
    },
    draft: {
      type: String,
      default: '',
    },
    sending: {
      type: Boolean,
      default: false,
    },
    initials: {
      type: String,
      default: '?',
    },
    canCompose: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    canSend() {
      return Boolean(String(this.draft || '').trim()) && !this.sending;
    },
  },
  methods: {
    avatarLetter(entry) {
      const name = String(entry.userName || entry.email || '').trim();
      return name ? name.charAt(0).toUpperCase() : '?';
    },
    onSend() {
      if (!this.canSend) return;
      this.$emit('send');
    },
    downloadFile(file) {
      const id = file?.id;
      if (!id || !this.$nova?.downloadFile) return;
      this.$nova.downloadFile(id);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimateActivityTab.scss';
</style>
